import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { forOwn } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
// eslint-disable-next-line import-whitelist -- Calibration options are loaded and updated from content
import { SettingsService } from '../../core/h3ml/dsl/sensor-fusion/view-settings/settings.service';
import {
  getInputData,
  Input,
  loadAssetBoundingBoxForOutput,
} from '../../cross-team-shared/misc-utils/task-data-utils';
import { getDefaultCalibrationOptions } from '../../cross-team-shared/state/setting-state';
import { Point3D } from '../../team-apis/otterdam/answers/shape-typings';
import { CalibrationData } from '../../team-apis/otterdam/calibration-data';
import {
  SensorLocationsV0,
  SensorLocationsV1,
  WXYZQuaternionTuple,
} from '../../team-apis/otterdam/locations-data';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { ObjectWith } from '../utils/misc-utils';
import { Location } from './sensor-location';

@Injectable()
export class LoadTaskDataService extends InjectableSuperclass {
  private store = inject(PointCloudContentStore);
  private settingsService = inject(SettingsService);
  private outputName = this.store.state().output.name;

  loadTaskData() {
    this.store.assign({
      assetBoundingBox: this.loadAssetBoundingBox(),
      calibrationData: this.loadCalibrationData(),
      locations: this.loadLocations(),
    });

    this.updateCameraInitialRotations();
  }

  private loadAssetBoundingBox() {
    return loadAssetBoundingBoxForOutput(
      this.outputName,
      this.store.state().task,
    );
  }

  private loadCalibrationData() {
    const calibrationData: ObjectWith<CalibrationData> = {};

    for (const key of this.getMatchingKeys(Input.CalibrationParameters)) {
      const data = this.getInputData<CalibrationData>(key);
      if (!data) {
        continue;
      }

      const outputName = extractOutputName(key, Input.CalibrationParameters);
      calibrationData[outputName] = data;

      // Sets default calibration options for each camera loaded from the task
      this.settingsService.setCalibrationOptions(
        outputName,
        getDefaultCalibrationOptions(),
      );
    }

    return calibrationData;
  }

  private loadCameraInitialRotations() {
    const initialRotations: ObjectWith<Point3D> = {};

    for (const key of this.getMatchingKeys(Input.InitialRotation)) {
      const data = this.getInputData<Point3D>(key);
      if (!data) {
        continue;
      }

      const videoOutputName = extractOutputName(key, Input.InitialRotation);
      initialRotations[videoOutputName] = data;
    }

    return initialRotations;
  }

  private loadLocations() {
    const key = `${this.outputName} ${Input.SensorLocations}`;
    const locations = this.getInputData<
      SensorLocationsV0[] | SensorLocationsV1
    >(key);

    if (Array.isArray(locations)) {
      return locations.map(convertToSensorLocation);
    } else if (locations) {
      return locations.perFrame;
    }

    return undefined;
  }

  private getInputData<InputDataType>(key: string): InputDataType | undefined {
    return getInputData<InputDataType>(key, this.store.state().task);
  }

  private getMatchingKeys(substring: string) {
    const task = this.store.state().task;
    return Object.keys(task.data).filter((key) => key.includes(substring));
  }

  private updateCameraInitialRotations() {
    const initialRotations = this.loadCameraInitialRotations();

    const calibrationOptions = this.store.state().render.calibrationOptions;
    forOwn(calibrationOptions, (_, videoOutputName) => {
      const initialRotation = initialRotations[videoOutputName];
      if (isDefined(initialRotations[videoOutputName])) {
        this.settingsService.updateCalibrationOptions(videoOutputName, {
          initialRotation,
        });
      }
    });
  }
}

// Extracts the output name from a computed input key string
// Example: Video Output 123 Calibration Data -> Video Output 123
function extractOutputName(key: string, inputName: string) {
  const words = key.split(' ');
  const inputNameWordLength = inputName.split(' ').length;
  return words.slice(0, words.length - inputNameWordLength).join(' ');
}

function convertToSensorLocation(location: SensorLocationsV0): Location {
  const [W, ...XYZ] = location.slice(3) as WXYZQuaternionTuple;

  return {
    position: location.slice(0, 3) as Point3D,
    rotation: [...XYZ, W],
    timestamp: 0,
  };
}
